import { io, Socket } from 'socket.io-client';

export type ClientType = 'station-proxy' | 'music';

export interface WebSocketMessage {
  type: string;
  payload?: any;
}

export interface AuthenticationData {
  userId: string;
  clientType: ClientType;
  token?: string;
}

export interface WebSocketConfig {
  url: string;
  userId: string;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onMessage?: (message: WebSocketMessage & { from: ClientType; userId: string; timestamp: number }) => void;
  onError?: (error: string) => void;
}

class StationWebSocketClient {
  private socket: Socket | null = null;
  private config: WebSocketConfig | null = null;
  // Keep retrying forever — this runs on an always-on kiosk, so a capped
  // attempt count meant a transient blip on reload could leave it permanently
  // disconnected.
  private maxReconnectAttempts = Infinity;

  /**
   * Connect to WebSocket server
   */
  connect(config: WebSocketConfig): void {
    // Already connected for the same user — nothing to do.
    if (this.socket?.connected && this.config?.userId === config.userId) {
      console.warn('[Station WS] Already connected');
      return;
    }

    // Always fully tear down any previous socket first. Calling io() again
    // without this leaves an orphaned socket that keeps reconnecting in the
    // background; its listeners still fire and fight the new socket, which
    // shows up as connect/disconnect flapping (and sometimes a stuck state).
    this.teardownSocket();

    this.config = config;

    // Create socket connection
    this.socket = io(config.url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1000,
    });

    // Authentication is sent from the 'connect' handler once the socket is up.
    this.setupEventListeners();
  }

  /** Remove listeners and drop any existing socket so none are left orphaned. */
  private teardownSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket || !this.config) return;

    this.socket.on('connect', () => {
      console.log('[Station WS] Connected');
      this.authenticate();
    });

    this.socket.on('authenticated', (data) => {
      console.log('[Station WS] Authenticated:', data);
      this.config?.onConnected?.();
    });

    this.socket.on('message', (message) => {
      console.log('[Station WS] Message received:', message);
      this.config?.onMessage?.(message);
    });

    this.socket.on('client-connected', (data) => {
      console.log('[Station WS] Another client connected:', data);
    });

    this.socket.on('error', (error) => {
      console.error('[Station WS] Error:', error);
      this.config?.onError?.(error.message || 'Unknown error');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Station WS] Disconnected:', reason);
      this.config?.onDisconnected?.();
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[Station WS] Reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[Station WS] Reconnection failed');
      this.config?.onError?.('Failed to reconnect to server');
    });
  }

  private authenticate(): void {
    if (!this.socket?.connected || !this.config) {
      console.log('[Station WS] Cannot authenticate: socket not connected or no config');
      return;
    }

    const authData: AuthenticationData = {
      userId: this.config.userId,
      clientType: 'music',
    };

    console.log('[Station WS] Sending authentication:', authData);
    this.socket.emit('authenticate', authData);
  }

  sendMessage(type: string, payload?: any): void {
    if (!this.socket?.connected) {
      console.warn('[Station WS] Not connected. Message not sent.');
      return;
    }

    const message: WebSocketMessage = {
      type,
      payload,
    };

    this.socket.emit('message', message);
    console.log('[Station WS] Message sent:', message);
  }

  disconnect(): void {
    this.teardownSocket();
    this.config = null;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  ping(): void {
    if (!this.socket?.connected) {
      console.warn('[Station WS] Not connected. Ping not sent.');
      return;
    }

    this.socket.emit('ping');
  }
}

export const stationWebSocketClient = new StationWebSocketClient();
